/**
 * Reading the legend.
 *
 * `GET /legend` publishes every definition the console uses, keyed by a page scoped id.
 * These three helpers turn that response into something a popover can look an id up in.
 * They live here rather than inside one feature because two screens now show the same
 * sentences and a second copy of this index is a second place for a definition to rot.
 *
 * A missing id renders the trigger with no popover. A gap in the legend is a gap in the
 * legend, not a crash on the screen.
 */
import type { LegendEntry, LegendResponse } from '@elutide/shared';

export type LegendIndex = Readonly<Record<string, LegendEntry>>;

/** Entries by id. An absent response yields an empty index, which every caller tolerates. */
export function indexLegend(response: LegendResponse | undefined): LegendIndex {
  if (!response) return {};
  const index: Record<string, LegendEntry> = {};
  for (const entry of response.entries) index[entry.id] = entry;
  return index;
}

export function definitionFor(index: LegendIndex, id: string | undefined): string | null {
  if (!id) return null;
  return index[id]?.definition ?? null;
}

/** The line under the sentence in a popover: what unit it is in and what it is measured on. */
export function detailFor(index: LegendIndex, id: string | undefined): string | null {
  if (!id) return null;
  const entry = index[id];
  if (!entry) return null;
  const parts = [entry.unit, entry.basis].filter((part) => part.length > 0);
  if (entry.pending_owner) parts.push('default, pending Todd');
  return parts.length > 0 ? parts.join(' . ') : null;
}
